import React, { createContext, useContext, useState } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../Stores/LLMClassifierBuilderStore";
import { llmClassifierVersionLabel } from "../utils/llmClassifierVersionDisplay";
import { useSaveFlow } from "./SaveFlow";
import { UnsavedChangesDialog } from "./UnsavedChangesDialog";

interface VersionSwitch {
    /** Switches the loaded version, holding behind the unsaved-changes dialog when needed. */
    selectVersion: (implementationId: string) => void;
    isLoadingVersion: boolean;
}

const VersionSwitchContext = createContext<VersionSwitch | undefined>(
    undefined,
);

export function useVersionSwitch(): VersionSwitch {
    const context = useContext(VersionSwitchContext);
    if (!context) {
        throw new Error(
            "useVersionSwitch must be used within VersionSwitchContext.Provider",
        );
    }
    return context;
}

/**
 * Owns the one guarded version switch so every entry point that can load a different version —
 * the version history list and the older-draft banner's "Back to" link — goes through the same
 * unsaved-changes check instead of each reimplementing it. Called once per builder page; expose
 * the result via `VersionSwitchContext.Provider` and render `dialog` alongside the page content.
 */
export function useCreateVersionSwitch(): VersionSwitch & {
    dialog: React.ReactNode;
} {
    const store = useStore(LLMClassifierBuilderStore);
    const { triggerSave } = useSaveFlow();
    const [pendingVersionId, setPendingVersionId] = useState<string>();
    const [isLoadingVersion, setIsLoadingVersion] = useState(false);

    const loadVersion = async (implementationId: string) => {
        setIsLoadingVersion(true);
        try {
            await store.loadVersion(implementationId);
        } finally {
            setIsLoadingVersion(false);
            setPendingVersionId(undefined);
        }
    };

    const selectVersion = (implementationId: string) => {
        if (store.classifier.implementation.id === implementationId) return;
        if (store.hasUnsavedChanges()) {
            setPendingVersionId(implementationId);
            return;
        }
        void loadVersion(implementationId);
    };

    const dialog = (
        <UnsavedChangesDialog
            isOpen={!!pendingVersionId}
            isDiscarding={isLoadingVersion}
            targetVersionLabel={llmClassifierVersionLabel(
                store.llmClassifierVersions.find(
                    (version) => version.id === pendingVersionId,
                )?.createdOn,
            )}
            onClose={() => setPendingVersionId(undefined)}
            onDiscard={() => {
                if (pendingVersionId) void loadVersion(pendingVersionId);
            }}
            onSaveFirst={() => {
                const versionId = pendingVersionId;
                // Leave only the save dialog open, rather than stacking it on top of this one.
                setPendingVersionId(undefined);
                if (versionId) triggerSave(() => void loadVersion(versionId));
            }}
        />
    );

    return { selectVersion, isLoadingVersion, dialog };
}

export { VersionSwitchContext };
